import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, UploadCloud, X } from "lucide-react";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { useUploadPeriod, useUploadJobStatus, useActiveUploadJob } from "./usePeriods";

interface Props {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: (periodId: string) => void;
}

type SlotKey = "usage" | "emergency" | "checkin" | "teamAlert";

const SLOT_LABELS: Record<SlotKey, string> = {
  usage: "Usage",
  emergency: "Emergency",
  checkin: "Check-in",
  teamAlert: "Team Alert",
};

// Checked in this order so more specific patterns (emergency, team alert,
// check-in) win over the broad "usage" fallback pattern.
const SLOT_PATTERNS: [SlotKey, RegExp][] = [
  ["emergency", /emergency/i],
  ["teamAlert", /team[\s_-]?alert/i],
  ["checkin", /check[\s_-]?in/i],
  ["usage", /usage|device_usage/i],
];

function detectSlot(filename: string): SlotKey | null {
  for (const [slot, pattern] of SLOT_PATTERNS) {
    if (pattern.test(filename)) return slot;
  }
  return null;
}

const EMPTY_SLOTS: Record<SlotKey, File | null> = {
  usage: null,
  emergency: null,
  checkin: null,
  teamAlert: null,
};

const ACCEPTED_EXT = /\.(csv|xlsx|xls)$/i;

export function UploadPeriodDialog({
  clientId,
  open,
  onOpenChange,
  onUploaded,
}: Props): React.JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [slots, setSlots] = useState<Record<SlotKey, File | null>>(EMPTY_SLOTS);
  // Files we couldn't confidently categorise (unrecognised name, or a
  // duplicate of a slot already filled) — the user assigns these manually
  // rather than us silently guessing wrong.
  const [unassigned, setUnassigned] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const upload = useUploadPeriod(clientId);

  // Set once a usage-CSV upload returns its jobId; a feature-only upload
  // finishes synchronously and never sets this. Drives the polling status
  // below and switches the dialog into a progress view instead of closing
  // immediately, since the real processing hasn't happened yet.
  const [jobId, setJobId] = useState<string | null>(null);
  const jobStatus = useUploadJobStatus(clientId, jobId);

  // In-memory jobId doesn't survive the dialog closing, a page refresh, or a
  // new tab. Whenever the dialog is (re)opened with nothing tracked yet,
  // check whether this client already has a job running in the background
  // (started from an earlier dialog instance) and resume watching it instead
  // of showing an empty file picker over a upload that's still going.
  const activeJob = useActiveUploadJob(clientId);
  useEffect(() => {
    if (open && jobId === null && activeJob.jobId) setJobId(activeJob.jobId);
  }, [open, jobId, activeJob.jobId]);

  useEffect(() => {
    if (!jobId || !jobStatus.data) return;
    if (jobStatus.data.status === "completed") {
      void qc.invalidateQueries({ queryKey: ["periods", clientId] });
      void qc.invalidateQueries({ queryKey: ["clients"] });
      const newestPeriodId = jobStatus.data.completedPeriodIds.at(-1);
      setJobId(null);
      reset();
      onOpenChange(false);
      if (newestPeriodId) onUploaded(newestPeriodId);
    } else if (jobStatus.data.status === "failed") {
      setJobId(null);
      setSubmitError(
        jobStatus.data.errorMessage ?? "Processing failed partway through. Try uploading again.",
      );
    } else if (jobStatus.data.stalled) {
      setJobId(null);
      setSubmitError(
        "Processing seems to have stalled. It may still finish in the background — check back shortly, or try uploading again.",
      );
    }
  }, [jobId, jobStatus.data]);

  const reset = (): void => {
    setSubmitError(null);
    setSlots(EMPTY_SLOTS);
    setUnassigned([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addFiles = (files: FileList | File[]): void => {
    const nextSlots = { ...slots };
    const nextUnassigned = [...unassigned];

    for (const file of Array.from(files)) {
      if (!ACCEPTED_EXT.test(file.name)) continue;
      const detected = detectSlot(file.name);
      if (detected && !nextSlots[detected]) {
        nextSlots[detected] = file;
      } else {
        nextUnassigned.push(file);
      }
    }

    setSlots(nextSlots);
    setUnassigned(nextUnassigned);
    setSubmitError(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };

  const assignUnassigned = (index: number, slot: SlotKey): void => {
    const file = unassigned[index];
    if (!file) return;
    setSlots((prev) => ({ ...prev, [slot]: file }));
    setUnassigned((prev) => prev.filter((_, i) => i !== index));
  };

  const discardUnassigned = (index: number): void => {
    setUnassigned((prev) => prev.filter((_, i) => i !== index));
  };

  const clearSlot = (slot: SlotKey): void => {
    setSlots((prev) => ({ ...prev, [slot]: null }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);

    const anyFile = slots.usage ?? slots.emergency ?? slots.checkin ?? slots.teamAlert;
    if (!anyFile) {
      setSubmitError("Drop or select at least one CSV file to upload.");
      return;
    }
    if (unassigned.length > 0) {
      setSubmitError("Assign a type to every file below before uploading, or remove it.");
      return;
    }

    try {
      const result = await upload.mutateAsync({
        clientId,
        files: {
          usageCsv: slots.usage ?? undefined,
          emergencyCsv: slots.emergency ?? undefined,
          checkinCsv: slots.checkin ?? undefined,
          teamAlertCsv: slots.teamAlert ?? undefined,
        },
      });
      if ("jobId" in result) {
        // Usage CSV present: processing hasn't happened yet. Keep the dialog
        // open and switch to polling — the effect above closes it and
        // navigates once the job actually completes.
        setJobId(result.jobId);
        return;
      }
      reset();
      onOpenChange(false);
      const mostRecent = result.periods[result.periods.length - 1];
      if (mostRecent) onUploaded(mostRecent.id);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Upload failed. Try again.");
    }
  };

  const filledCount = Object.values(slots).filter(Boolean).length;
  // Busy for the initial upload request AND for as long as its job is still
  // being processed — the file is fully handed off well before the data is
  // actually ready, so "done uploading" isn't "done processing."
  const isProcessingJob = jobId !== null;
  const isBusy = upload.isPending || isProcessingJob;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Ignore close attempts (backdrop click, X, Escape) while an upload is in
        // flight — closing the dialog wouldn't cancel the request, it would just
        // make the eventual result (or error) appear with no visible context.
        if (isBusy) return;
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogHeader>
        <DialogTitle>Upload usage data</DialogTitle>
        <DialogDescription>
          Drop in any of the usage, emergency, check-in, and team alert CSVs — we&rsquo;ll figure
          out which is which from the filename. At least one file is required. Month is detected
          automatically from the dates inside the files.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 mt-2">
        <fieldset disabled={isBusy} className="min-w-0 space-y-4">
          <div
            role="button"
            tabIndex={0}
            aria-label="Drop CSV files here, or click to browse"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center transition-colors ${
              isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"
            }`}
          >
            <UploadCloud className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium">Drag CSVs here, or click to browse</p>
            <p className="text-xs text-muted-foreground">
              Usage, Emergency, Check-in, Team Alert — any combination, .csv or .xlsx
            </p>
            <input
              id="upload-files"
              ref={fileInputRef}
              type="file"
              multiple
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
              }}
            />
          </div>

          {filledCount > 0 ? (
            <ul className="space-y-1.5">
              {(Object.keys(SLOT_LABELS) as SlotKey[])
                .filter((slot) => slots[slot])
                .map((slot) => (
                  <li
                    key={slot}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  >
                    <span>
                      <span className="font-medium">{SLOT_LABELS[slot]}:</span>{" "}
                      <span className="text-muted-foreground">{slots[slot]?.name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        clearSlot(slot);
                      }}
                      aria-label={`Remove ${SLOT_LABELS[slot]} file`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
            </ul>
          ) : null}

          {unassigned.length > 0 ? (
            <ul className="space-y-1.5">
              {unassigned.map((file, index) => (
                <li
                  key={`${file.name}-${String(index)}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 px-3 py-2 text-sm"
                >
                  <span className="min-w-0 truncate text-muted-foreground">{file.name}</span>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Select
                      className="h-8 w-[140px]"
                      aria-label={`Type for ${file.name}`}
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) assignUnassigned(index, e.target.value as SlotKey);
                      }}
                    >
                      <option value="" disabled>
                        Assign type…
                      </option>
                      {(Object.keys(SLOT_LABELS) as SlotKey[])
                        .filter((slot) => !slots[slot])
                        .map((slot) => (
                          <option key={slot} value={slot}>
                            {SLOT_LABELS[slot]}
                          </option>
                        ))}
                    </Select>
                    <button
                      type="button"
                      onClick={() => {
                        discardUnassigned(index);
                      }}
                      aria-label={`Discard ${file.name}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </fieldset>

        {upload.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Uploading…
          </p>
        ) : null}

        {isProcessingJob ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {jobStatus.data
              ? `Processing month ${String(Math.min(jobStatus.data.completedMonths + 1, jobStatus.data.totalMonths))} of ${String(jobStatus.data.totalMonths)}…`
              : "Processing your upload…"}{" "}
            Don&rsquo;t close this window.
          </p>
        ) : null}

        {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isBusy}>
            {isBusy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
